import { SideNav } from "components";
import { HomeWrapper } from "components/CdBalance/style";
import {
  LargeButton,
  OverlayLoader,
  PageTitle,
  Ptag,
} from "components/core";
import NewRadioCard from "components/core/NewRadioCard";
import routes from "components/routes";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router";
import http from "services/httpService";

function Benefits() {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(true);
  const [pickVoluntary] = useState(false);
  const [productList, setProductList] = useState([]);
  const [mandatoryPicked, setMandatoryPicked] = useState(false);
  const [selectedMandatory, setSelectedMandatory] = useState(new Set());
  const [selectedVoluntary, setSelectedVoluntary] = useState(new Set());
  const [plan, setPlan] = useState(false);
  const [stateRestored, setStateRestored] = useState(false);

  const isAddProductFlow = useMemo(() => {
    const params = new URLSearchParams(document.location.search);
    const path = params.get("path");
    if (path && path === "add") return true;
    return false;
  }, []);

  // Helper function to remove duplicates between mandatory and voluntary selections
  const removeDuplicatesFromVoluntary = (mandatorySet, voluntarySet) => {
    const cleanedVoluntary = new Set();
    const mandatoryIds = new Set(Array.from(mandatorySet).map(item => item.product_type_id));
    
    voluntarySet.forEach(item => {
      if (!mandatoryIds.has(item.product_type_id)) {
        cleanedVoluntary.add(item);
      }
    });
    
    if (cleanedVoluntary.size !== voluntarySet.size) {
      console.log(`Removed ${voluntarySet.size - cleanedVoluntary.size} duplicate(s) from voluntary selection`);
    }
    
    return cleanedVoluntary;
  };

  // Helper functions for persistence - only save at key moments, not on every change
  const saveCurrentState = useCallback(() => {
    try {
      // Clean duplicates before saving
      const cleanedVoluntary = removeDuplicatesFromVoluntary(selectedMandatory, selectedVoluntary);
      
      const state = {
        selectedMandatory: Array.from(selectedMandatory),
        selectedVoluntary: Array.from(cleanedVoluntary),
        mandatoryPicked,
        pickVoluntary,
        timestamp: Date.now()
      };
      localStorage.setItem("benefits_state", JSON.stringify(state));
    } catch (error) {
      console.log("Error saving state:", error);
    }
  }, [selectedMandatory, selectedVoluntary, mandatoryPicked, pickVoluntary]);

  const loadSavedState = useCallback((productList) => {
    try {
      const savedState = localStorage.getItem("benefits_state");
      if (!savedState || productList.length === 0) {
        setStateRestored(true);
        return;
      }
      
      const state = JSON.parse(savedState);

      // Check if the saved state is from the last 30 minutes (to avoid stale data)
      if (Date.now() - state.timestamp > 30 * 60 * 1000) {
        localStorage.removeItem("benefits_state");
        setStateRestored(true);
        return;
      }
      
      // Restore mandatory selections
      let mandatorySet = new Set();
      if (state.selectedMandatory && state.selectedMandatory.length > 0) {
        state.selectedMandatory.forEach(savedProduct => {
          const matchingProduct = productList.find(
            product => product.product_type_name === savedProduct.product_type_name
          );
          if (matchingProduct) {
            matchingProduct.mandatory = savedProduct.mandatory;
            mandatorySet.add(matchingProduct);
          }
        });
      }
      
      // Restore voluntary selections and clean duplicates
      let voluntarySet = new Set();
      if (state.selectedVoluntary && state.selectedVoluntary.length > 0) {
        state.selectedVoluntary.forEach(savedProduct => {
          const matchingProduct = productList.find(
            product => product.product_type_name === savedProduct.product_type_name
          );
          if (matchingProduct) {
            matchingProduct.mandatory = savedProduct.mandatory;
            voluntarySet.add(matchingProduct);
          }
        });
        
        // Clean duplicates before setting state
        voluntarySet = removeDuplicatesFromVoluntary(mandatorySet, voluntarySet);
      }
      
      // Set the cleaned selections
      setSelectedMandatory(mandatorySet);
      setSelectedVoluntary(voluntarySet);
      
    
      
      // Use setTimeout to ensure all state updates have been applied
      setTimeout(() => {
        setStateRestored(true);
      }, 100);
      
    } catch (error) {
      console.log("Error loading saved state:", error);
      localStorage.removeItem("benefits_state");
      setStateRestored(true);
    }
  }, []);

  const filterProductList = useCallback((data) => {
    let newArray = data.map((obj) => ({ ...obj, mandatory: true }));
    if (isAddProductFlow) {
      let alreadySelectedProducts = localStorage.getItem("gmc_details");
      alreadySelectedProducts = JSON.parse(alreadySelectedProducts);
      alreadySelectedProducts = Object.keys(alreadySelectedProducts);
      newArray = newArray.filter(
        (prod) => !alreadySelectedProducts.includes(prod.product_type_name)
      );
      setProductList(newArray);
      return;
    }
    setProductList(newArray);
  }, [isAddProductFlow]);

  const getProductList = useCallback(async () => {
    try {
      const { data } = await http.get("business/product_type_selectables/");
      localStorage.setItem("product_list", JSON.stringify(data));
      filterProductList(data);
    } catch (err) {
      alert("Something went wrong");
    } finally {
      setLoader(false);
    }
  }, [filterProductList]);

  useEffect(() => {
    (async function () {
      setLoader(true);
      const plan = localStorage.getItem("global_plan_type");
      if (plan === "Flex") {
        setPlan(true);
      } else {
        setPlan(false);
      }
      await getProductList();
      setLoader(false);
    })();
    
    // If there's no saved state at all, mark as restored immediately
    const savedState = localStorage.getItem("benefits_state");
    if (!savedState) {
      setStateRestored(true);
    }
  }, [getProductList]);

  // Load saved state only after product list is ready
  useEffect(() => {
    if (productList.length > 0 && !stateRestored) {
      loadSavedState(productList);
    }
  }, [productList, stateRestored, loadSavedState]);

  // Clean duplicates whenever selectedMandatory changes
  useEffect(() => {
    if (selectedMandatory.size > 0) {
      const cleanedVoluntary = removeDuplicatesFromVoluntary(selectedMandatory, selectedVoluntary);
      if (cleanedVoluntary.size !== selectedVoluntary.size) {
        setSelectedVoluntary(cleanedVoluntary);
      }
    }
  }, [selectedMandatory, selectedVoluntary]);

  // Save state when component is about to unmount or page is about to unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (selectedMandatory.size > 0 || selectedVoluntary.size > 0) {
        saveCurrentState();
      }
    };

    // Save state when navigating away
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Save state when component unmounts
      if (selectedMandatory.size > 0 || selectedVoluntary.size > 0) {
        saveCurrentState();
      }
    };
  }, [selectedMandatory, selectedVoluntary, mandatoryPicked, pickVoluntary, saveCurrentState]);

  // Function to remove product from backend when deselected
  const removeProductFromBackend = async (product) => {
    try {
      // Check if this product was previously saved to backend
      const savedState = localStorage.getItem("benefits_state");
      if (savedState) {
        const state = JSON.parse(savedState);
        const wasPreviouslySelected = state.selectedMandatory?.some(p => p.product_type_name === product.product_type_name) ||
                                    state.selectedVoluntary?.some(p => p.product_type_name === product.product_type_name);
        
        if (wasPreviouslySelected) {
          // Get userplan_id from various possible sources
          let userplanId = null;
          
          // Try to get from eb_selected_values (most likely source)
          const selectedValues = localStorage.getItem("eb_selected_values");
          if (selectedValues) {
            const parsedValues = JSON.parse(selectedValues);
            const userplansData = parsedValues.userplans_data;
            if (userplansData) {
              // Find the userplan_id for this product
              for (const [userplanIdKey, userplanData] of Object.entries(userplansData)) {
                if (userplanData.product_type?.product_type_name === product.product_type_name) {
                  userplanId = userplanIdKey;
                  break;
                }
              }
            }
          }
          
          // Fallback to other localStorage sources
          if (!userplanId) {
            userplanId = localStorage.getItem("userplan_id") || 
                        localStorage.getItem("flex_userplan_id") ||
                        localStorage.getItem("eb_current_plan");
          }
          
          if (userplanId) {
            const payload = {
              userplan_id: userplanId
            };
            
          
            await http.post("/business/delete_userplan/", payload);
       
          } else {
            console.warn("Could not find userplan_id for product removal:", product.product_type_name);
          }
        }
      }
    } catch (error) {
      console.error("Error removing product from backend:", error);
      // Don't show error to user as this is a background operation
      // The product will still be deselected in the UI
    }
  };

  const optionHandler = async (value, list, set, flag) => {
    const newSet = new Set(list);
    if (list.has(value)) {
      // Product is being deselected - remove from backend
      await removeProductFromBackend(value);
      newSet.delete(value);
    } else {
      value.mandatory = flag;
      newSet.add(value);
      
      // If adding to mandatory, remove from voluntary if it exists
      if (flag === true) {
        const updatedVoluntary = removeDuplicatesFromVoluntary(newSet, selectedVoluntary);
        if (updatedVoluntary.size !== selectedVoluntary.size) {
          setSelectedVoluntary(updatedVoluntary);
        }
      }
    }
    
    set(newSet);
    
    // Save state after user makes a selection (with small delay to batch rapid selections)
    setTimeout(() => {
      const currentMandatory = flag ? newSet : selectedMandatory;
      const currentVoluntary = !flag ? newSet : selectedVoluntary;
      
      // Clean duplicates before saving
      const cleanedVoluntary = removeDuplicatesFromVoluntary(currentMandatory, currentVoluntary);
      
      const state = {
        selectedMandatory: Array.from(currentMandatory),
        selectedVoluntary: Array.from(cleanedVoluntary),
        mandatoryPicked,
        pickVoluntary,
        timestamp: Date.now()
      };
      localStorage.setItem("benefits_state", JSON.stringify(state));
    }, 500);
  };

  const selectedMandatoryDetailsArray = useMemo(() => {
    // For voluntary selection, show only products that are NOT already selected as mandatory
    return productList.filter((product) => !selectedMandatory.has(product));
  }, [productList, selectedMandatory]);

  const postApiCall = async (data) => {
    const postData = Array.from(data);
  
    // Log each item in the array
    postData.forEach((item, index) => {
      console.log(`postData[${index}]:`, item);
    });
    
    if (postData.length === 0) {
    
      // Check if we have data in localStorage that we should use
      const savedState = localStorage.getItem("benefits_state");
      if (savedState) {
        const state = JSON.parse(savedState);
       
        // Try to recover from saved state
        if (state.selectedMandatory?.length > 0 || state.selectedVoluntary?.length > 0) {
          const recoveredData = [...(state.selectedMandatory || []), ...(state.selectedVoluntary || [])];
          if (recoveredData.length > 0) {
            // Retry the API call with recovered data
            postApiCall(new Set(recoveredData));
            return;
          }
        }
      }
      
      alert("No products selected. Please select at least one product.");
      return;
    }
    
    setLoader(true);
    try {
      await http.post(
        "business/setup_plan_product/",
        postData
      );
      
      // Navigate first
      navigate(
        `/${routes.UNIVERSAL_PLAN_SETUP}${isAddProductFlow ? "?path=add" : ""}`
      );
    
    } catch (e) {
      console.log("API call failed:", e);
    } finally {
      setLoader(false);
    }
  };

  const submitHandler = () => {
  
    // Clean duplicates before proceeding
    const cleanedVoluntary = removeDuplicatesFromVoluntary(selectedMandatory, selectedVoluntary);
    if (cleanedVoluntary.size !== selectedVoluntary.size) {
      setSelectedVoluntary(cleanedVoluntary);
    }
    
    if (!plan) {
    
      saveCurrentState();
      postApiCall(selectedMandatory);
      return;
    }
    
    if (!mandatoryPicked) {
      setMandatoryPicked(true);
      
      // Scroll to top when moving from mandatory to voluntary products
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Check if there are any products left for voluntary selection
      const remainingProducts = productList.filter((product) => !selectedMandatory.has(product));
      
      // If no voluntary products available, auto-navigate to next screen
      if (remainingProducts.length === 0) {
        console.log("No voluntary products available, auto-navigating to next screen");
        const combinedArray = [...selectedMandatory, ...cleanedVoluntary];
        
        // Save final state before API call
        const finalState = {
          selectedMandatory: Array.from(selectedMandatory),
          selectedVoluntary: Array.from(cleanedVoluntary),
          mandatoryPicked: true,
          pickVoluntary: true,
          timestamp: Date.now()
        };
        localStorage.setItem("benefits_state", JSON.stringify(finalState));
        
        postApiCall(combinedArray);
        return;
      }
      
      // Save state immediately with updated flag
      const updatedState = {
        selectedMandatory: Array.from(selectedMandatory),
        selectedVoluntary: Array.from(cleanedVoluntary),
        mandatoryPicked: true, // Use the new value directly
        pickVoluntary: pickVoluntary,
        timestamp: Date.now()
      };
      localStorage.setItem("benefits_state", JSON.stringify(updatedState));
      return;
    }
    
    // We're in voluntary phase, proceed to final submission
    console.log("Proceeding to final submission with mandatory and voluntary products");
    const combinedArray = [...selectedMandatory, ...cleanedVoluntary];
   
    // Save final state before API call
    const finalState = {
      selectedMandatory: Array.from(selectedMandatory),
      selectedVoluntary: Array.from(cleanedVoluntary),
      mandatoryPicked: true,
      pickVoluntary: true,
      timestamp: Date.now()
    };
    localStorage.setItem("benefits_state", JSON.stringify(finalState));
    
    postApiCall(combinedArray);
  };

  // === Button disabled logic ===
  const isButtonDisabled = (() => {
    if (!plan) return selectedMandatory.size === 0;
    if (!mandatoryPicked) return selectedMandatory.size === 0;
    // If we're in voluntary selection phase, button should always be enabled
    // (user can proceed with just mandatory products or with mandatory + voluntary)
    return false;
  })();

  return (
    <>
      {(loader || !stateRestored) ? (
        <OverlayLoader />
      ) : (
        <section>
          <SideNav />
          <HomeWrapper>
            <PageTitle
              title={`${isAddProductFlow ? "Add" : "Select"} Products`}
            />
            <div style={{ maxWidth: "932px" }}></div>
            <section style={{ paddingBottom: "5rem" }}>
              {plan && (
                <Ptag fontSize="16px" fontWeight="600" marginTop="20px">
                  {`${
                    mandatoryPicked
                      ? "Choose Voluntary Products "
                      : "Choose Mandatory Products  "
                  }`}
                </Ptag>
              )}
              <div style={{ marginTop: "30px", maxWidth: "932px" }}>
                {!mandatoryPicked && (
                  <NewRadioCard
                    list={productList}
                    selected={selectedMandatory}
                    onChange={(val) =>
                      optionHandler(
                        val,
                        selectedMandatory,
                        setSelectedMandatory,
                        true
                      )
                    }
                  />
                )}
                {mandatoryPicked && selectedMandatoryDetailsArray.length > 0 && (
                  <section>
                    <div style={{ marginTop: "2rem" }}>
                      <NewRadioCard
                        list={selectedMandatoryDetailsArray}
                        selected={selectedVoluntary}
                        onChange={(val) =>
                          optionHandler(
                            val,
                            selectedVoluntary,
                            setSelectedVoluntary,
                            false
                          )
                        }
                      />
                    </div>
                  </section>
                )}
              </div>
            </section>
            <footer
              style={{
                maxWidth: "100%",
                marginTop: "1rem",
                width: "calc(100% - 8rem)",
              }}
            >
              <LargeButton
                onClick={submitHandler}
                label="Save & Next"
                disabled={isButtonDisabled}
                style={{
                  width: "932px",
                  padding: "18px 0px",
                  opacity: isButtonDisabled ? 0.6 : 1,
                  pointerEvents: isButtonDisabled ? "none" : "auto",
                }}
              />
            </footer>
          </HomeWrapper>
        </section>
      )}
    </>
  );
}

export default Benefits;